'use client';

import React, { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { AuthComponentProps } from '../types'
import { 
  TextInput, 
  PasswordInput, 
  Button, 
  Stack, 
  Paper, 
  Title, 
  Divider,
  Group,
  Text,
  Anchor,
  Modal,
  Center,
  Alert
} from '@mantine/core'
import { IconBrandGoogle, IconAlertCircle } from '@tabler/icons-react'
import { validateEmail, validatePassword } from '../utils/validation'
import { handleServiceError } from '../utils/errorHandler'

interface LoginProps extends AuthComponentProps {
  onRegisterClick?: () => void
  title?: string
  logo?: React.ReactNode
}

export const Login: React.FC<LoginProps> = ({ 
  onSuccess, 
  onError,
  onRegisterClick,
  title = "Welcome Back",
  logo
}) => {
  const { signInWithGoogle, signInWithEmail, forgotPassword } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [formError, setFormError] = useState('')
  const [resetSuccess, setResetSuccess] = useState(false)
  const [resetError, setResetError] = useState('')

  const handleGoogleLogin = async () => {
    setLoading(true)
    setFormError('')
    
    try {
      await signInWithGoogle()
      onSuccess?.()
    } catch (error) {
      const handledError = handleServiceError(error, 'Failed to sign in with Google')
      setFormError(handledError.message)
      onError?.(handledError)
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const emailValidation = validateEmail(email)
    const passwordValidation = validatePassword(password)

    setEmailError(emailValidation.error)
    setPasswordError(passwordValidation.error)

    return emailValidation.isValid && passwordValidation.isValid
  }

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    
    setLoading(true)
    setFormError('')
    
    try {
      await signInWithEmail(email, password)
      onSuccess?.()
    } catch (error) {
      const handledError = handleServiceError(error, 'Failed to sign in')
      setFormError(handledError.message)
      onError?.(handledError)
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    const emailValidation = validateEmail(resetEmail)
    if (!emailValidation.isValid) {
      setResetError(emailValidation.error)
      return
    }
    
    setResetLoading(true)
    setResetError('')
    setResetSuccess(false)
    
    try {
      await forgotPassword(resetEmail)
      setResetSuccess(true)
    } catch (error) {
      const handledError = handleServiceError(error, 'Failed to send password reset email')
      setResetError(handledError.message)
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <>
      <Paper radius="md" p="xl" withBorder>
        {logo && (
          <Center mb="md">
            {logo}
          </Center>
        )}
        <Title order={2} ta="center" mt="md" mb={50}>
          {title}
        </Title>
        
        {formError && (
          <Alert 
            icon={<IconAlertCircle size={16} />} 
            color="red" 
            mb="md"
            withCloseButton
            onClose={() => setFormError('')}
          >
            {formError}
          </Alert>
        )}
        
        <form onSubmit={handleEmailLogin}>
          <Stack>
            <TextInput
              required
              label="Email"
              placeholder="hello@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              radius="md"
            />

            <PasswordInput
              required
              label="Password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={passwordError}
              radius="md"
            />

            <Group justify="space-between">
              <Anchor 
                component="button" 
                type="button"
                c="dimmed" 
                size="sm"
                onClick={() => setResetModalOpen(true)}
              >
                Forgot password?
              </Anchor>
            </Group>

            <Button type="submit" fullWidth radius="md" loading={loading}>
              Sign in
            </Button>
          </Stack>
        </form>

        <Divider label="Or continue with" labelPosition="center" my="lg" />

        <Button 
          leftSection={<IconBrandGoogle size={16} />}
          variant="outline"
          fullWidth
          onClick={handleGoogleLogin}
          radius="md"
          loading={loading}
        >
          Continue with Google
        </Button>

        <Text c="dimmed" size="sm" ta="center" mt="md">
          Don&apos;t have an account?{' '}
          <Anchor component="button" type="button" onClick={onRegisterClick}>
            Create account
          </Anchor>
        </Text>
      </Paper>

      <Modal 
        opened={resetModalOpen} 
        onClose={() => setResetModalOpen(false)}
        title="Reset password"
      >
        <Stack>
          {resetSuccess ? (
            <Alert color="green" title="Success">
              Password reset instructions have been sent to your email.
            </Alert>
          ) : (
            <>
              {resetError && (
                <Alert 
                  icon={<IconAlertCircle size={16} />} 
                  color="red"
                  withCloseButton
                  onClose={() => setResetError('')}
                >
                  {resetError}
                </Alert>
              )}
              <TextInput
                required
                label="Email"
                placeholder="Enter your email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                error={resetError && !resetEmail ? 'Email is required' : ''}
              />
              <Button 
                onClick={handleForgotPassword}
                loading={resetLoading}
                fullWidth
              >
                Send reset instructions
              </Button>
            </>
          )}
        </Stack>
      </Modal>
    </>
  )
} 